import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissionsContext } from './PermissionsProvider';

/**
 * @param {Function} [onRetry] — what the Retry button should re-run. Defaults to
 *   refetching /permissions/me, which is right for the permissions gates but
 *   WRONG for a guard whose readiness came from a different query: `refresh()`
 *   invalidates only ['permissions','me'], so a guard gated on, say,
 *   GET /declarant would render a Retry button that refetches something else and
 *   leaves the actual failure in place. Such callers pass their own `refetch`.
 */
const PermissionsErrorState = ({ onRetry }) => {
  const { t } = useTranslation('common');
  const { refresh } = usePermissionsContext();
  const [isRetrying, setIsRetrying] = useState(false);

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      await (onRetry ? onRetry() : refresh());
    } finally {
      setIsRetrying(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 px-6 text-center">
      <div
        className="max-w-md"
        role="alert"
      >
        <h1 className="mb-3 text-2xl font-semibold text-gray-900">
          {t('common:permissionsError.title', 'Unable to verify permissions')}
        </h1>
        <p className="mb-6 text-sm leading-6 text-gray-600">
          {t(
            'common:permissionsError.body',
            'We could not load your permissions. Retry before assuming this page is unavailable.',
          )}
        </p>
        <button
          type="button"
          className="rounded-md bg-primaryColor px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primaryColor-dark disabled:cursor-not-allowed disabled:opacity-60"
          onClick={handleRetry}
          disabled={isRetrying}
        >
          {isRetrying
            ? t('common:permissionsError.retrying', 'Retrying...')
            : t('common:permissionsError.retry', 'Retry')}
        </button>
      </div>
    </div>
  );
};

export default PermissionsErrorState;
